import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

// RxJS
import { Observable, of, throwError } from 'rxjs';

// Services
import { AuthService } from './auth.service';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Roles } from '../models/roles.enum';


@Injectable({
  providedIn: 'root',
})
export class AuthGuard  {

  /**
   * Creates an instance of AuthGuard.
   *
   * @param router
   * @param authService
   * @param constants
   * @param sharedProfilesService
   * @memberof AuthGuard
   */
  constructor(
    private router: Router,
    private authService: AuthService,
  ) { }

  /**
   * Indicates if a route can be activated
   *
   * @param route
   * @param state
   * @returns
   * @memberof AuthGuardService
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const url: string = state.url;
    if (url === '/authentication/logout' || url === '/authentication/tosForm') {
      return of(true);
    }
    return this.checkAuthorization(url);
  }

  /**
   * Indicates if a child route can be activated
   *
   * @param childRoute
   * @param state
   * @returns
   * @memberof AuthGuard
   */
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const url: string = state.url;
    return this.checkAuthorization(url);
  }

  /**
   * Check if user is logged in
   *
   * @param url
   * @returns
   * @memberof AuthGuardService
   */
  checkLogin(url: string): boolean {
    if (this.authService.isLoggedIn()) {
      return true;
    }

    // Store the attempted URL for redirecting
    this.authService.redirectUrl = url;

    // Navigate to the login page with extras
    this.router.navigate(['authentication', 'login']);
    return false;
  }


  checkAuthorization(url: string): Observable<boolean> {
    if (url === '/mobile-start') {
      return of(true);
    }
    if (!this.checkLogin(url)) {
      return of(false);
    }
    return this.authService.getProfile().pipe(
      map(p => {
        if(p.isSuperuser){
          return true;
        }else if (!this.authService.isRole(Roles.admin) && !this.authService.isRole(Roles.projectmanager)
        && !this.authService.isRole(Roles.responsregconstr)) {
          this.router.navigate(['authentication', 'unauthorized']);
          return false;
        } else if (!p.isTosAccepted) {
          this.router.navigate(['authentication', 'tosForm']);
          return false;
        } else if (((url.includes('/companies') && !url.includes('/companiesStats'))
        || url.includes('/users')) && !this.authService.isRole(Roles.admin)) {
          this.router.navigate(['authentication', 'unauthorized']);
          return false;
        } else {
          return true;
        }
      }),
      catchError(error => {
        if (error instanceof HttpErrorResponse && [403, 404].includes(error.status)) {
          this.router.navigate(['authentication', 'unauthorized']);
        }

        return throwError(error);
      })
    );
  }
}
